import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { PingLog } from '../types';
import { formatResponseTime, formatDate } from '../utils/api';

interface StatusGraphProps {
  pingHistory: PingLog[];
  height?: number;
  showResponseTime?: boolean;
  showUptime?: boolean;
}

interface ChartDataPoint {
  time: string;
  response_time: number;
  is_up: number; // 1 for up, 0 for down
  status_code: number;
  timestamp: number;
}

const StatusGraph: React.FC<StatusGraphProps> = ({
  pingHistory,
  height = 300,
  showResponseTime = true,
  showUptime = true,
}) => {
  // Transform ping history into chart data
  const chartData = useMemo(() => {
    return pingHistory
      .slice(-50) // Show last 50 pings to avoid overcrowding
      .map((ping): ChartDataPoint => ({
        time: formatDate(ping.created_at),
        response_time: ping.response_time,
        is_up: ping.is_up ? 1 : 0,
        status_code: ping.status_code,
        timestamp: new Date(ping.created_at).getTime(),
      }))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [pingHistory]);

  // Calculate uptime percentage for the displayed data
  const uptimePercentage = useMemo(() => {
    if (chartData.length === 0) return 0;
    const successfulPings = chartData.filter(point => point.is_up === 1).length;
    return Math.round((successfulPings / chartData.length) * 100);
  }, [chartData]);

  // Calculate average response time
  const averageResponseTime = useMemo(() => {
    if (chartData.length === 0) return 0;
    const totalResponseTime = chartData.reduce((sum, point) => sum + point.response_time, 0);
    return Math.round(totalResponseTime / chartData.length);
  }, [chartData]);

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-base-200 p-3 border border-base-300 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-sm">
            Status: <span className={data.is_up === 1 ? 'text-success' : 'text-error'}>
              {data.is_up === 1 ? 'UP' : 'DOWN'}
            </span>
          </p>
          <p className="text-sm">Response: {formatResponseTime(data.response_time)}</p>
          <p className="text-sm">Code: {data.status_code}</p>
        </div>
      );
    }
    return null;
  };

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-base-200 rounded-lg">
        <div className="text-center text-base-content/60">
          <p>No ping data available</p>
          <p className="text-sm">Add a monitor to start collecting data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary statistics */}
      <div className="grid grid-cols-2 gap-4">
        <div className="stat bg-base-200 rounded-lg">
          <div className="stat-title">Uptime</div>
          <div className="stat-value text-lg">{uptimePercentage}%</div>
        </div>
        <div className="stat bg-base-200 rounded-lg">
          <div className="stat-title">Avg Response</div>
          <div className="stat-value text-lg">{formatResponseTime(averageResponseTime)}</div>
        </div>
      </div>

      {/* Response Time Chart */}
      {showResponseTime && (
        <div className="bg-base-100 p-4 rounded-lg border border-base-300">
          <h3 className="text-lg font-semibold mb-4">Response Time History</h3>
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="time" 
                stroke="#9CA3AF"
                fontSize={12}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }}
              />
              <YAxis 
                stroke="#9CA3AF"
                fontSize={12}
                tickFormatter={(value) => formatResponseTime(value)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="response_time" 
                stroke="#3B82F6" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: '#3B82F6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Uptime Chart */}
      {showUptime && (
        <div className="bg-base-100 p-4 rounded-lg border border-base-300">
          <h3 className="text-lg font-semibold mb-4">Uptime History</h3>
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="time" 
                stroke="#9CA3AF"
                fontSize={12}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                }}
              />
              <YAxis 
                stroke="#9CA3AF"
                fontSize={12}
                domain={[0, 1]}
                tickFormatter={(value) => value === 1 ? 'UP' : 'DOWN'}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="is_up" 
                stroke="#10B981" 
                fill="#10B981"
                fillOpacity={0.3}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Data points summary */}
      <div className="text-sm text-base-content/70">
        Showing last {chartData.length} pings • 
        {chartData.length > 0 && (
          <>
            {' '}From {formatDate(chartData[0].time)} to {formatDate(chartData[chartData.length - 1].time)}
          </>
        )}
      </div>
    </div>
  );
};

export default StatusGraph; 